import { Box, Button, Popover, Typography } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useState } from 'react';
import { DateRangePicker } from '@src/components';
import { colors } from '@src/styles';
import { fadeInSX } from '@src/styles/animations';
import { useRunsContext } from '../context/RunsContext';
import { OrganizationPicker } from './OrganizationPicker';
import { PeriodPreset } from '../hooks/dto/runItem.dto';
import { monoFontFamily, titleFontFamily } from '../utils/format';

const PRESETS: Exclude<PeriodPreset, 'custom'>[] = ['24h', '7d', '30d'];

const controlHeight = 36;

export function ScopePeriodBar() {
  const {
    scope,
    setScope,
    organizationId,
    organizationNames,
    setOrganizationId,
    flowName,
    period,
    setPreset,
    setCustomRange,
    periodError,
  } = useRunsContext();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [start, setStart] = useState<Date | null>(new Date(period.since));
  const [end, setEnd] = useState<Date | null>(new Date(period.until));

  const crumb = (active: boolean, label: string, onClick: () => void) => (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      title={label}
      sx={{
        all: 'unset',
        boxSizing: 'border-box',
        maxWidth: 260,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        cursor: active ? 'default' : 'pointer',
        fontFamily: monoFontFamily,
        fontSize: '12px',
        color: active ? colors.primary.yellow : colors.brand.textSecondary,
        transition: 'color 0.15s ease',
        '&:hover': { color: active ? colors.primary.yellow : colors.primary.white },
        '&:focus-visible': { outline: `1px solid ${colors.primary.yellow}`, borderRadius: '4px' },
      }}
    >
      {label}
    </Box>
  );
  const separator = <ChevronRightIcon sx={{ fontSize: 16, color: colors.brand.textMuted }} />;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: titleFontFamily,
            fontSize: '22px',
            fontWeight: 500,
            letterSpacing: '-0.025em',
            lineHeight: 1.15,
            color: colors.primary.white,
          }}
        >
          Runs
        </Typography>
        <Box component="nav" aria-label="Escopo" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          {crumb(scope === 'all', 'organizações', () => setOrganizationId(undefined))}
          {organizationId && separator}
          {organizationId &&
            crumb(scope === 'org', organizationNames[organizationId] ?? organizationId, () => setScope('org'))}
          {flowName && separator}
          {flowName && crumb(scope === 'auto', flowName, () => setScope('auto'))}
        </Box>
      </Box>

      {scope !== 'all' && (
        <Box sx={fadeInSX()}>
          <OrganizationPicker />
        </Box>
      )}

      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          role="group"
          aria-label="Período"
          sx={{
            display: 'flex',
            height: controlHeight,
            boxSizing: 'border-box',
            border: `1px solid ${colors.brand.line}`,
            backgroundColor: colors.brand.surface,
            borderRadius: '8px',
            p: '3px',
            gap: '2px',
          }}
        >
          {PRESETS.map((p) => {
            const isActive = period.preset === p;
            return (
              <Button
                key={p}
                onClick={() => setPreset(p)}
                aria-pressed={isActive}
                sx={{
                  minWidth: 0,
                  px: 1.5,
                  height: '100%',
                  borderRadius: '6px',
                  fontFamily: monoFontFamily,
                  fontSize: '12px',
                  textTransform: 'none',
                  color: isActive ? colors.primary.yellow : colors.brand.textSecondary,
                  backgroundColor: isActive ? 'rgba(255, 160, 21, 0.12)' : 'transparent',
                  '&:hover': {
                    backgroundColor: isActive ? 'rgba(255, 160, 21, 0.18)' : 'rgba(255, 255, 255, 0.05)',
                    color: isActive ? colors.primary.yellow : colors.primary.white,
                  },
                }}
              >
                {p}
              </Button>
            );
          })}
        </Box>
        <Button
          startIcon={<CalendarMonthOutlinedIcon sx={{ color: 'inherit !important' }} />}
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-pressed={period.preset === 'custom'}
          sx={{
            height: controlHeight,
            px: 1.5,
            textTransform: 'none',
            fontFamily: titleFontFamily,
            fontSize: '13px',
            fontWeight: 500,
            borderRadius: '8px',
            color: period.preset === 'custom' ? colors.primary.yellow : colors.brand.textSecondary,
            backgroundColor: period.preset === 'custom' ? 'rgba(255, 160, 21, 0.12)' : colors.brand.surface,
            border: `1px solid ${period.preset === 'custom' ? 'rgba(255, 160, 21, 0.45)' : colors.brand.line}`,
            '&:hover': { borderColor: 'rgba(255, 160, 21, 0.5)', color: colors.primary.white },
          }}
        >
          Personalizado
        </Button>
        <Popover
          open={!!anchor}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                marginTop: 1,
                backgroundColor: colors.brand.surface,
                backgroundImage: 'none',
                border: `1px solid ${colors.brand.line}`,
                borderRadius: '12px',
                boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
              },
            },
          }}
        >
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography
              sx={{
                fontFamily: monoFontFamily,
                fontSize: '10px',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: colors.brand.textMuted,
              }}
            >
              Período personalizado
            </Typography>
            <DateRangePicker
              startDate={start}
              endDate={end}
              onStartDateChange={setStart}
              onEndDateChange={setEnd}
              startDateLabel="De"
              endDateLabel="Até"
            />
            {periodError && (
              <Typography sx={{ color: colors.status.rejected, fontSize: '12px' }}>{periodError}</Typography>
            )}
            <Button
              variant="contained"
              disableElevation
              sx={{
                height: 36,
                borderRadius: '8px',
                backgroundColor: colors.primary.yellow,
                color: colors.brand.ink,
                fontFamily: titleFontFamily,
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { backgroundColor: '#FFB94A' },
              }}
              onClick={() => {
                if (start && end) {
                  setCustomRange(start, end);
                }
              }}
            >
              Aplicar
            </Button>
          </Box>
        </Popover>
      </Box>
    </Box>
  );
}
